// Bound both the connection and JSON-body read. Writes are deliberately handled
// by the existing API client and never retried or cancelled by this helper.
export async function readRequest(fetcher, input, init = {}, timeoutMs = 30000) {
  const controller = new AbortController();
  const caller = init.signal;
  const abort = () => controller.abort(caller?.reason);
  if (caller?.aborted) abort();
  else caller?.addEventListener('abort', abort, { once: true });
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error('The server is taking too long to respond. Please retry.');
      error.name = 'TimeoutError';
      reject(error);
      controller.abort();
    }, timeoutMs);
  });
  try {
    return await Promise.race([timeout, (async () => {
      const response = await fetcher(input, { ...init, signal: controller.signal });
      if (response.headers?.get('content-type')?.includes('application/json')) {
        const body = await response.arrayBuffer();
        return new Response(body, { status: response.status, statusText: response.statusText, headers: response.headers });
      }
      return response;
    })()]);
  } finally {
    clearTimeout(timer);
    caller?.removeEventListener('abort', abort);
  }
}

export function sharedDirectoryReads(fetcher) {
  const pending = new Map();
  return (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = String(init.method || 'GET').toUpperCase();
    const run = () => ['GET','HEAD'].includes(method)
      ? readRequest(fetcher, input, init, init.timeoutMs || 30000)
      : fetcher(input, init);
    if (method !== 'GET') { pending.clear(); return run(); }
    // Only share simultaneous directory/settings reads; never retain user data
    // after completion, and keep independently cancellable requests separate.
    if (init.signal || !/\/(settings|users\/directory)$/.test(url)) return run();
    const key = `${url}|${init.credentials || 'include'}|${JSON.stringify(init.headers || {})}`;
    if (!pending.has(key)) {
      const promise = run();
      pending.set(key, promise);
      promise.then(() => { if (pending.get(key) === promise) pending.delete(key); },
        () => { if (pending.get(key) === promise) pending.delete(key); });
    }
    return pending.get(key).then(response => response.clone());
  };
}
